import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CCard } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilChevronRight } from "@coreui/icons";
import type { TrackerResultsDto, TrackerStrategyDto } from "../../../api/hooks/usePortfolioTracker";
import { portfolioTrackerStrategyPath } from "../../../routes/route-paths";
import {
  formatDay,
  formatPercent,
  formatSignedMoney,
  formatSignedPercent,
  toneOf,
} from "../portfolio-tracker";
import { useTradeTally } from "../useTradeTally";
import { TrackerStrategyIdent } from "./TrackerStrategyIdent";

interface StrategyTableProps {
  results: TrackerResultsDto;
  strategies: Map<string, TrackerStrategyDto>;
}

/** Every strategy in the portfolio, each row opening that strategy's own page. */
export function StrategyTable({ results, strategies }: StrategyTableProps) {
  const { t } = useTranslation("portfolioTracker");
  const tally = useTradeTally();
  const gained = results.strategies.some((strategy) => strategy.shareOfGainsPercent !== null);

  const label = {
    allocation: t("table.allocation"),
    from: t("table.from"),
    trades: t("table.trades"),
    profit: t("table.profit"),
    share: t("table.share"),
  };

  return (
    <CCard className="tracker-table">
      <div className="tracker-table-title">
        <h3 className="h6 mb-0">{t("table.title")}</h3>
        <p className="mb-0">{t("table.description")}</p>
      </div>
      <div className="tracker-table-head">
        <span>{t("table.strategy")}</span>
        <span>{label.allocation}</span>
        <span>{label.from}</span>
        <span>{label.trades}</span>
        <span className="text-end">{label.profit}</span>
        <span className="text-end">{label.share}</span>
        <span />
      </div>
      {results.strategies.map((result) => {
        const strategy = strategies.get(result.tradingStrategyId);
        if (!strategy) return null;
        return (
          <Link
            key={result.tradingStrategyId}
            to={portfolioTrackerStrategyPath(result.tradingStrategyId)}
            className="tracker-table-row"
          >
            <span className="tracker-cell-strategy">
              <TrackerStrategyIdent strategy={strategy} />
            </span>
            <span className="tracker-cell" data-label={label.allocation}>
              <b>{formatPercent(result.allocationPercent)}</b>
              <small>{t("table.ofBalance")}</small>
            </span>
            <span className="tracker-cell" data-label={label.from}>
              <b>{formatDay(result.start)}</b>
            </span>
            <span className="tracker-cell" data-label={label.trades}>
              <b>{t("tally.closed", { count: result.won + result.lost })}</b>
              <small>{tally(result)}</small>
            </span>
            <span className={`tracker-cell is-number ${toneOf(result.profit)}`} data-label={label.profit}>
              <b>{formatSignedMoney(result.profit)}</b>
            </span>
            <span className="tracker-cell is-number" data-label={label.share}>
              {result.shareOfGainsPercent !== null ? (
                <b>{formatPercent(result.shareOfGainsPercent)}</b>
              ) : (
                <>
                  <b>—</b>
                  <small>{result.profit <= -0.5 ? t("results.down") : t("table.noChange")}</small>
                </>
              )}
            </span>
            <span className="tracker-open">
              {t("table.open")}
              <CIcon icon={cilChevronRight} size="sm" />
            </span>
          </Link>
        );
      })}
      <div className="tracker-table-row is-total">
        <span className="tracker-cell-strategy">
          <b>{t("table.total")}</b>
        </span>
        <span className="tracker-cell" data-label={label.allocation}>
          <b>{formatPercent(100 - results.cashPercent)}</b>
          <small>
            {results.cashPercent > 0
              ? t("table.keptAsCash", { percent: formatPercent(results.cashPercent) })
              : t("table.ofBalance")}
          </small>
        </span>
        <span className="tracker-cell is-gap" />
        <span className="tracker-cell" data-label={label.trades}>
          <b>{t("tally.closed", { count: results.closedTrades })}</b>
          <small>{t("tally.wonLost", { won: results.won, lost: results.lost })}</small>
        </span>
        <span className={`tracker-cell is-number ${toneOf(results.profit)}`} data-label={label.profit}>
          <b>{formatSignedMoney(results.profit)}</b>
          <small>{formatSignedPercent((results.profit / results.putIn) * 100)}</small>
        </span>
        <span className="tracker-cell is-number" data-label={label.share}>
          <b>{gained ? "100%" : "—"}</b>
        </span>
        <span className="is-gap" />
      </div>
    </CCard>
  );
}
